<template>
    <div class="flex flex-col h-full select-none text-xs">

        <!-- Scope dropdown + counter -->
        <div class="flex items-center justify-between px-2 py-1 border-b border-white/10 shrink-0 gap-2">
            <select
                v-model="scope"
                class="bg-slate-700 border border-white/10 text-slate-200 rounded px-1 py-0.5 text-xs flex-1 min-w-0"
            >
                <option value="global">Global</option>
                <option value="object" :disabled="!selectedUuid">Selected Object</option>
                <option value="page"   :disabled="!selectedPageUuid">Selected Page</option>
            </select>
            <span class="text-slate-500 shrink-0">{{ cursor + 1 }}/{{ store.entries.length }}</span>
        </div>

        <!-- Category filter chips -->
        <div class="flex flex-wrap gap-1 px-2 py-1 border-b border-white/10 shrink-0">
            <button v-for="cat in CATEGORIES" :key="cat.key"
                @click="toggleCategory(cat.key)"
                class="px-1.5 py-0.5 rounded transition-colors text-xs"
                :class="activeCategories.has(cat.key)
                    ? 'bg-slate-600 text-slate-200'
                    : 'bg-slate-800 text-slate-600 line-through'"
            >{{ cat.label }}</button>
        </div>

        <!-- Object label row (object scope only) -->
        <div v-if="scope === 'object' && objectLabel"
             class="px-2 py-0.5 text-slate-400 border-b border-white/10 shrink-0 truncate">
            {{ objectLabel }}
        </div>

        <!-- Empty state -->
        <div v-if="visibleEntries.length === 0"
             class="flex-1 flex items-center justify-center text-slate-500">
            {{ emptyMessage }}
        </div>

        <!-- Timeline -->
        <div v-else class="flex-1 overflow-y-auto hide-scrollbar py-1 min-h-0">
            <div class="relative">
                <div class="absolute bg-slate-600" style="left: 22px; top: 16px; bottom: 16px; width: 1px;" />

                <div
                    v-for="item in visibleEntries"
                    :key="item.globalIndex"
                    :ref="el => { if (item.globalIndex === cursor) currentEntryEl = el }"
                    @click="selectEntry(item)"
                    @dblclick="jumpTo(item.globalIndex)"
                    class="relative flex items-center gap-2 pr-3 py-1 cursor-pointer rounded mx-1 transition-colors"
                    :class="{
                        'bg-blue-600/20':  item.globalIndex === cursor && selectedEntry?.globalIndex !== item.globalIndex,
                        'bg-blue-600/35 ring-1 ring-blue-400/40': selectedEntry?.globalIndex === item.globalIndex,
                        'hover:bg-white/5': selectedEntry?.globalIndex !== item.globalIndex,
                        'opacity-35': item.globalIndex > cursor,
                    }"
                    style="padding-left: 10px;"
                >
                    <!-- Timeline dot -->
                    <div
                        class="shrink-0 relative z-10 rounded-full flex items-center justify-center transition-all text-center"
                        style="width: 14px; height: 14px;"
                        :class="{
                            'bg-blue-500 ring-2 ring-blue-300/40': item.globalIndex === cursor,
                            'bg-slate-500': item.globalIndex < cursor,
                            'bg-slate-700 border border-slate-500': item.globalIndex > cursor,
                        }"
                    >
                        <div v-if="item.globalIndex === cursor" class="rounded-full bg-white" style="width: 5px; height: 5px;" />
                    </div>

                    <!-- Icon + description -->
                    <div class="flex-1 min-w-0 leading-tight">
                        <div
                            class="truncate flex items-center gap-1"
                            :class="{
                                'text-white font-medium': item.globalIndex === cursor,
                                'text-slate-300': item.globalIndex < cursor,
                                'text-slate-500': item.globalIndex > cursor,
                            }"
                        >
                            <span class="shrink-0" style="font-size: 11px;">{{ entryIcon(item.entry) }}</span>
                            <span class="truncate">{{ item.entry.description || 'Edit' }}</span>
                        </div>
                        <div class="text-slate-600" style="font-size: 10px;">state {{ item.globalIndex + 1 }}</div>
                    </div>

                    <!-- "now" badge -->
                    <span v-if="item.globalIndex === cursor"
                          class="shrink-0 text-blue-300 font-medium" style="font-size: 10px;">now</span>
                </div>
            </div>
        </div>

        <!-- Detail panel (shown when an entry is selected) -->
        <div v-if="selectedEntry"
             class="border-t border-white/10 px-2 py-2 space-y-1 shrink-0 bg-slate-800/60">
            <div class="text-slate-400 font-medium truncate">{{ selectedEntry.entry.description }}</div>
            <template v-if="selectedEntry.entry.changedProperties?.length">
                <template v-for="cp in selectedEntry.entry.changedProperties.slice(0, 4)" :key="cp.key">
                    <!-- hass_config: show structured HA diff -->
                    <template v-if="cp.key === 'hass_config'">
                        <div class="text-slate-500" style="font-size: 10px;">
                            <span class="text-slate-400">properties:</span>
                            {{ cp.prev?.properties?.length ?? 0 }} → {{ cp.next?.properties?.length ?? 0 }}
                        </div>
                        <div class="text-slate-500" style="font-size: 10px;">
                            <span class="text-slate-400">events:</span>
                            {{ cp.prev?.events?.length ?? 0 }} → {{ cp.next?.events?.length ?? 0 }}
                        </div>
                    </template>
                    <!-- Normal property diff -->
                    <div v-else class="text-slate-500 flex gap-1 min-w-0">
                        <span class="text-slate-400 shrink-0">{{ cp.key.replace(/[:_]/g, ' ') }}:</span>
                        <span class="truncate text-red-400/70">{{ formatValue(cp.prev) }}</span>
                        <span class="text-slate-600">→</span>
                        <span class="truncate text-green-400/70">{{ formatValue(cp.next) }}</span>
                    </div>
                </template>
            </template>
        </div>

        <!-- Restore Version button (object scope only) -->
        <div v-if="scope === 'object' && selectedEntry && selectedEntry.globalIndex !== cursor"
             class="border-t border-white/10 px-2 py-1.5 shrink-0">
            <button
                @click="$emit('restore-version', selectedEntry.globalIndex)"
                class="w-full text-center text-xs py-1 rounded bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 transition-colors"
            >
                🔄 Restore Version
            </button>
        </div>

    </div>
</template>

<script setup>
import { computed, watch, ref, nextTick } from 'vue';
import { useUndoStore } from '../history/undoStore';
import { matchesObject, matchesPage } from '../history/matchesObject';

const props = defineProps({
    selectedUuid:     { type: String, default: null },
    selectedPageUuid: { type: String, default: null },
    objectLabel:      { type: String, default: null },
});
const emit = defineEmits(['jump-to', 'restore-version', 'navigate']);

const CATEGORIES = [
    { key: 'layout',        label: '↔ Layout' },
    { key: 'styling',       label: '🎨 Styling' },
    { key: 'structure',     label: '🧱 Structure' },
    { key: 'homeassistant', label: '🏠 HA' },
];

const store    = useUndoStore();
const cursor   = computed(() => store.cursor);
const scope    = ref('global');
const selectedEntry    = ref(null);
const activeCategories = ref(new Set(CATEGORIES.map(c => c.key)));

function toggleCategory(key) {
    const s = new Set(activeCategories.value);
    s.has(key) ? s.delete(key) : s.add(key);
    activeCategories.value = s;
}

function entryCategory(entry) {
    if (Array.isArray(entry.children)) {
        // Batch: use first child's category
        return entry.children[0]?.category ?? 'structure';
    }
    return entry.category ?? 'styling';
}

// Reset scope and selection when relevant props change
watch(() => props.selectedUuid,     () => { if (scope.value === 'object') selectedEntry.value = null; });
watch(() => props.selectedPageUuid, () => { if (scope.value === 'page')   selectedEntry.value = null; });
watch(scope, () => { selectedEntry.value = null; });

const visibleEntries = computed(() => {
    let all = store.entries.map((entry, globalIndex) => ({ entry, globalIndex }));
    if (scope.value === 'object' && props.selectedUuid)
        all = all.filter(({ entry }) => matchesObject(entry, props.selectedUuid));
    if (scope.value === 'page' && props.selectedPageUuid)
        all = all.filter(({ entry }) => matchesPage(entry, props.selectedPageUuid));
    // Apply category filter (all entries pass if all categories are active)
    if (activeCategories.value.size < CATEGORIES.length)
        all = all.filter(({ entry }) => activeCategories.value.has(entryCategory(entry)));
    return all;
});

const emptyMessage = computed(() => {
    if (scope.value === 'object') return 'No history for this object';
    if (scope.value === 'page')   return 'No history for this page';
    return 'No history yet';
});

const ICON_MAP = {
    'Move':                   '↔',
    'Resize':                 '📏',
    'Change Color':           '🎨',
    'Change Text':            '📝',
    'Edit property':          '✏️',
    'Edit properties':        '✏️',
    'Restore Version':        '🔄',
    'Copy object':            '📋',
    'Batch':                  '📦',
    'Entity Binding Changed': '🏠',
    'Event Changed':          '⚡',
    'HA Config Changed':      '🏠',
};
const OBJ_ICON_MAP = {
    'Add':    '➕',
    'Delete': '🗑',
};
const PAGE_ICON_MAP = {
    'Add page':    '📄',
    'Delete page': '❌',
    'Restore:':    '🔄',
};

function entryIcon(entry) {
    const d = entry.description ?? '';
    if (ICON_MAP[d]) return ICON_MAP[d];
    for (const [prefix, icon] of Object.entries(OBJ_ICON_MAP)) {
        if (d.startsWith(prefix)) return icon;
    }
    for (const [prefix, icon] of Object.entries(PAGE_ICON_MAP)) {
        if (d.toLowerCase().startsWith(prefix.toLowerCase())) return icon;
    }
    if (d.includes('color') || d.includes('Color')) return '🎨';
    if (d.includes('text')  || d.includes('Text'))  return '📝';
    return '✏️';
}

function formatValue(val) {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'string' && val.length > 14) return val.slice(0, 12) + '…';
    return String(val);
}

/** Extract the first object UUID and page UUID from any entry type. */
function entryNavigation(entry) {
    // Batch: delegate to first child that has navigation data
    if (Array.isArray(entry.children)) {
        for (const child of entry.children) {
            const nav = entryNavigation(child);
            if (nav.objectUuid || nav.pageUuid) return nav;
        }
        return {};
    }
    return {
        objectUuid: entry.objectUuid ?? entry.affectedUuids?.[0] ?? null,
        pageUuid:   entry.pageUuid ?? null,
    };
}

function selectEntry(item) {
    const isDeselect = selectedEntry.value?.globalIndex === item.globalIndex;
    selectedEntry.value = isDeselect ? null : item;
    if (!isDeselect) {
        const nav = entryNavigation(item.entry);
        emit('navigate', nav);
    }
}

function jumpTo(index) {
    selectedEntry.value = null;
    emit('jump-to', index);
}

const currentEntryEl = ref(null);
watch(cursor, async () => {
    await nextTick();
    currentEntryEl.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}, { flush: 'post' });
</script>
